import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from 'react'
import { 
  X, 
  Building, 
  Calendar, 
  DollarSign, 
  MapPin, 
  Users, 
  Clock, 
  FileText, 
  Briefcase,
  Copy,
  CheckCircle,
  User,
  Mail,
  AlertCircle
} from 'lucide-react'
import { apiService } from '@/services/api'
import type { ApprovedJobRequest } from '@/services/api'
import { generateCandidateRegistrationLink, copyLinkToClipboard } from '@/features/rh/lib/candidateLink'
import { formatDateSafe } from '@/shared/lib/dateUtils'
import CandidatesList from '@/features/rh/components/CandidatesList'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface ActiveJobDetailProps {
  jobId: number
  onClose: () => void
}

const ActiveJobDetail = ({ jobId, onClose }: ActiveJobDetailProps) => {
  const [job, setJob] = useState<ApprovedJobRequest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedLink, setCopiedLink] = useState(false)
  const [showCandidates, setShowCandidates] = useState(false)

  useEffect(() => {
    loadJobDetail()
  }, [jobId])

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    // Guardar el estilo original
    const originalStyle = window.getComputedStyle(document.body).overflow
    // Bloquear scroll
    document.body.style.overflow = 'hidden'
    
    // Restaurar al desmontar
    return () => {
      document.body.style.overflow = originalStyle
    }
  }, [])

  const loadJobDetail = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await apiService.getApprovedJobRequests()
      const jobDetail = data.find(j => j.id === jobId)
      
      if (jobDetail) {
        setJob(jobDetail)
      } else {
        setError('No se encontró la vacante')
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar detalle de vacante:', err)
      setError(err.message || 'Error al cargar el detalle de la vacante')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyPublicLink = async () => {
    const publicLink = await generateCandidateRegistrationLink(jobId)
    const success = await copyLinkToClipboard(publicLink)
    if (success) {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 3000)
    }
  }

  const getUrgencyConfig = (prioridad: ApprovedJobRequest['prioridad']): { color: ColorDeBadge; label: string } => {
    const urgencyConfig: Record<string, { color: ColorDeBadge; label: string }> = {
      baja: { color: 'gris', label: 'Baja' },
      media: { color: 'amarillo', label: 'Media' },
      alta: { color: 'naranja', label: 'Alta' },
      urgente: { color: 'rojo', label: 'Urgente' },
    }

    return (prioridad && urgencyConfig[prioridad]) || { color: 'gris', label: 'Sin prioridad' }
  }

  const formatSalary = (min: number | null, max: number | null) => {
    if (min && max && min > 0 && max > 0) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`
    } else if (min && min > 0) {
      return `Desde $${min.toLocaleString()}`
    } else if (max && max > 0) {
      return `Hasta $${max.toLocaleString()}`
    }
    return 'Salario a convenir'
  }

  // Removida función formatDate local - ahora usamos formatDateSafe de utils

  if (isLoading) {
    // CapaDeModal monta en el body, así que aquí sobra el createPortal a mano y
    // el style con position fixed que se puso para pelear el recorte.
    return (
      <CapaDeModal zIndex={9999} onClickFondo={onClose}>
        <div className="bg-white rounded-lg p-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600">Cargando detalle de la vacante...</p>
          </div>
        </div>
      </CapaDeModal>
    )
  }

  if (error || !job) {
    return (
      <CapaDeModal zIndex={9999} onClickFondo={onClose}>
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <h3 className="text-xl font-semibold text-gray-900">Error</h3>
            <p className="text-gray-600 text-center">{error || 'No se pudo cargar la vacante'}</p>
            <button
              onClick={onClose}
              className={estiloDeBoton('secundario')}
            >
              Cerrar
            </button>
          </div>
        </div>
      </CapaDeModal>
    )
  }

  const urgencyConfig = getUrgencyConfig(job.prioridad)

  const modalContent = (
    <>
      <CapaDeModal zIndex={9999} onClickFondo={onClose}>
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full my-8">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-xl">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="pr-12">
              <div className="flex items-center space-x-3 mb-2">
                <Briefcase className="w-8 h-8" />
                <Badge color={urgencyConfig.color}>{urgencyConfig.label}</Badge>
              </div>
              <h2 className="text-3xl font-bold mb-2">
                {job.puestoSolicitar || 'Sin título'}
              </h2>
              <div className="flex items-center space-x-4 text-blue-100">
                <div className="flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  {job.areaVacante || 'Sin Departamento/Área'}
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  {job.candidateCount || 0} candidato{(job.candidateCount || 0) !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(100vh-250px)] overflow-y-auto">
            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={handleCopyPublicLink}
                className={estiloDeBoton(copiedLink ? 'exito' : 'primario')}
              >
                {copiedLink ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    ¡Link copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar link de registro
                  </>
                )}
              </button>
              
              <button
                onClick={() => setShowCandidates(true)}
                className={estiloDeBoton('primario')}
              >
                <Users className="w-4 h-4" />
                Ver candidatos ({job.candidateCount || 0})
              </button>
            </div>

            {/* Quick Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-900">Rango Salarial</span>
                </div>
                <p className="text-lg font-bold text-green-700">
                  {formatSalary(job.salarioMinimo, job.salarioMaximo)}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <MapPin className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">Vacantes Disponibles</span>
                </div>
                <p className="text-lg font-bold text-blue-700">
                  {job.numeroPlazas || 1} vacante{(job.numeroPlazas || 1) !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Calendar className="w-5 h-5 text-purple-600 mr-2" />
                  <span className="text-sm font-medium text-purple-900">Fecha de Inicio</span>
                </div>
                <p className="text-lg font-bold text-purple-700">
                  {formatDateSafe(job.fechaRequeridaInicio, 'long')}
                </p>
              </div>
            </div>

            {/* Detailed Sections */}
            <div className="space-y-6">
              {/* Descripción */}
              {job.justificacionNecesidad && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-3">
                    <FileText className="w-6 h-6 text-blue-600 mr-2" />
                    <h3 className="text-xl font-semibold text-gray-900">Descripción de la Vacante</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {job.justificacionNecesidad}
                  </p>
                </div>
              )}

              {/* Información del Solicitante */}
              {job.solicitante && (
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <User className="w-6 h-6 text-blue-600 mr-2" />
                    <h3 className="text-xl font-semibold text-gray-900">Información del Solicitante</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Nombre completo</p>
                      <p className="text-gray-900 font-medium">
                        {job.solicitante.firstName} {job.solicitante.lastName}
                      </p>
                    </div>
                    {job.solicitante.email && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Correo electrónico</p>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-gray-900">{job.solicitante.email}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline Info */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900">Información Temporal</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fecha de creación</p>
                    <p className="text-gray-900 font-medium">{formatDateSafe(job.fechaCreacion, 'long')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Inicio requerido</p>
                    <p className="text-gray-900 font-medium">{formatDateSafe(job.fechaRequeridaInicio, 'long')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Estado actual</p>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <p className="text-green-700 font-medium">Vacante Activa</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 rounded-b-xl border-t flex justify-between items-center">
            <div className="text-sm text-gray-600">
              ID de Vacante: <span className="font-mono font-semibold text-gray-900">#{job.id}</span>
            </div>
            <button
              onClick={onClose}
              className={estiloDeBoton('secundario')}
            >
              Cerrar
            </button>
          </div>
        </div>
      </CapaDeModal>

      {/* Modal de Candidatos */}
      {showCandidates && (
        <CandidatesList
          jobRequestId={job.id}
          jobTitle={job.puestoSolicitar}
          isOpen={true}
          onClose={() => setShowCandidates(false)}
        />
      )}
    </>
  )

  // Renderizar el modal usando Portal para que aparezca fuera del flujo del DOM
  // El portal lo hace CapaDeModal.
  return modalContent
}

export default ActiveJobDetail
